class InfiniteScroll {
  constructor(scopeElement = null) {
    // Store the scope element (tab content) for this instance
    // This ensures this instance always operates on the same tab/collection
    this.scopeElement = scopeElement || window.activeCollectionTab || document;
    
    // Helper method to get the correct scope (active tab or document)
    this.getScope = function() {
      // Always use the scope that was set during construction
      return this.scopeElement;
    };
    
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    this.productGrid = scope.querySelector('#product-grid');
    this.currentCountElement = scope.querySelector('[data-current-count]');
    this.totalCountElement = scope.querySelector('[data-total-count]');
    this.progressFill = scope.querySelector('[data-progress-fill]');
    
    // Select loading text elements relative to the button
    this.loadingText = this.loadMoreBtn ? this.loadMoreBtn.querySelector('[data-loading-text]') : null;
    this.loadText = this.loadMoreBtn ? this.loadMoreBtn.querySelector('[data-load-text]') : null;
    this.isLoading = false;
    this.currentPage = 1;
    this.loadedProductCount = 0;
    this.initialized = false;
    
    // Global swatch cache persists across InfiniteScroll instances (e.g. tab switches)
    if (!window.swatchDataCache) {
      window.swatchDataCache = {};
    }
    this.swatchCache = window.swatchDataCache;
    
    this.init();
  }

  init() {
    if (!this.loadMoreBtn) {
      return;
    }
    
    if (this.initialized) {
      return;
    }
    
    // Mark button as initialized to prevent double initialization
    if (this.loadMoreBtn.dataset.infiniteScrollInit === 'true') {
      return;
    }
    this.loadMoreBtn.dataset.infiniteScrollInit = 'true';
    
    // Use event delegation on document to catch clicks - this bypasses any CSS blocking issues
    this.boundHandleLoadMore = this.handleLoadMore.bind(this);
    this.documentClickHandler = (e) => {
      // Check if click is on or inside the load more button
      const loadMoreBtn = e.target.closest('[data-load-more]');
      if (loadMoreBtn) {
        this.boundHandleLoadMore(e);
      }
    };
    
    // Attach to document with capture to catch it early
    document.addEventListener('click', this.documentClickHandler, true);
    
    this.initialized = true;
    
    // Initialize current counts from DOM
    if (this.currentCountElement) {
      this.loadedProductCount = parseInt(this.currentCountElement.textContent) || 0;
    }
    
    // Cache swatch data from the initial page render
    if (this.productGrid) {
      this.collectSwatchDataFromContainer(this.productGrid);
    }

    window.addEventListener('beforeunload', () => {
      this.saveScrollState();
    });

    // Only restore on genuine back/forward navigation, not on reload or first visit.
    // If bfcache serves the page (pageshow persisted=true), DOMContentLoaded never fires
    // again so this block is skipped entirely — nothing to do.
    const isBackNavigation = (() => {
      try {
        const nav = performance.getEntriesByType('navigation')[0];
        if (nav) return nav.type === 'back_forward';
      } catch(e) {}
      return window.performance && window.performance.navigation &&
             window.performance.navigation.type === 2;
    })();

    if (isBackNavigation) {
      this.restoreScrollState();
    }
  }

  async handleLoadMore(event, options = {}) {
    const { silent = false } = options;

    // Prevent any default behavior
    event.preventDefault();
    event.stopPropagation();

    if (this.isLoading) {
      return;
    }

    // Set a global flag to prevent facets.js from interfering
    // Skip for silent (auto-load) to avoid blocking facets renders
    if (!silent) {
      window.infiniteScrollLoading = true;
    }

    this.isLoading = true;
    if (!silent) {
      this.showLoading();
    }
    
    // Minimum new products before we auto-load another page, and
    // maximum consecutive auto-loads to prevent runaway requests.
    const MIN_APPEND_THRESHOLD = 4;
    const MAX_AUTO_LOADS = 5;
    let autoLoadCount = 0;
    let totalAppended = 0;
    
    try {
      while (true) {
        // Get the next page URL from the data attribute
        const nextPageUrl = this.loadMoreBtn.dataset.nextPageUrl;
        
        if (!nextPageUrl) {
          if (totalAppended === 0) {
            this.hideLoadMoreButton();
          }
          break;
        }
        
        // Parse the URL to ensure we have the full URL
        const url = new URL(nextPageUrl, window.location.origin);
        
        // Fetch the next page via AJAX
        const response = await fetch(url.href, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const html = await response.text();
        
        // Parse the response to extract products
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newProductGrid = doc.querySelector('#product-grid');
        
        if (!newProductGrid) {
          break;
        }
        
        // Cache swatch data from ALL products on this page before dedup filtering.
        // Products that get deduped away may still contain swatch data for groups
        // that will be needed by other products on this or future pages.
        this.collectSwatchDataFromContainer(newProductGrid);
        
        const newProducts = newProductGrid.querySelectorAll('.grid__item');
        const nextLoadMoreBtn = doc.querySelector('[data-load-more]');
        
        if (newProducts.length === 0) {
          this.hideLoadMoreButton();
          break;
        }
        
        // Add new products to the grid (with deduplication if grouping enabled)
        const result = this.appendNewProducts(newProducts);
        
        // Update counters and progress using the actual appended count
        if (result.appended > 0) {
          this.updateCounters(result.appended);
          this.updateProgress();
          totalAppended += result.appended;
        }
        
        // Update or hide load more button
        if (nextLoadMoreBtn) {
          this.updateLoadMoreButton(nextLoadMoreBtn);
        } else {
          this.hideLoadMoreButton();
        }
        
        // Apply rendering logic only to actually appended products
        if (result.products.length > 0) {
          this.renderNewProductsDirect(result.products);
        }
        
        this.currentPage++;

        // Check if we've reached the total and should hide the button
        const scope = this.getScope();
        const totalEl = scope.querySelector('[data-total-count]');
        const totalCount = totalEl ? parseInt(totalEl.textContent) || 0 : 0;
        if (totalCount > 0 && this.loadedProductCount >= totalCount) {
          this.hideLoadMoreButton();
          break;
        }
        
        // If too few new products were added (most were cross-page duplicates)
        // and more pages exist, automatically load the next page to accumulate
        // a meaningful batch. This compensates for smaller page sizes needed
        // to stay within Shopify's metaobject reference limit.
        if (totalAppended < MIN_APPEND_THRESHOLD && nextLoadMoreBtn && autoLoadCount < MAX_AUTO_LOADS) {
          autoLoadCount++;
          continue;
        }
        
        // Enough products loaded or no more pages — stop.
        break;
      }
      
    } catch (error) {
      console.error('Error loading more products:', error);
      if (!silent) {
        this.showError();
      }
    } finally {
      this.isLoading = false;
      if (!silent) {
        this.hideLoading();
      }
      // Clear the flag
      if (!silent) {
        window.infiniteScrollLoading = false;
      }
    }
  }

  /**
   * Silently load the next page in the background.
   * Used to pre-fetch page 2 after initial render, tab switch, or sort/filter change.
   */
  async autoLoadNextPage() {
    // Refresh the loadMoreBtn reference in case the DOM was replaced
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    if (!this.loadMoreBtn) return;

    const syntheticEvent = { preventDefault() {}, stopPropagation() {} };
    return this.handleLoadMore(syntheticEvent, { silent: true });
  }

  appendNewProducts(newProducts) {
    // ALWAYS get a fresh reference to the product grid since facets.js might replace it
    const scope = this.getScope();
    const currentGrid = scope.querySelector('#product-grid');
    
    if (!currentGrid) {
      console.error('Product grid not found in DOM! Cannot append products.');
      return { appended: 0, products: [] };
    }
    
    // Update our cached reference
    this.productGrid = currentGrid;
    
    // Ensure loading overlay is hidden before appending
    const loadingOverlay = scope.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
    
    // Check if product grouping deduplication is enabled
    const container = scope.querySelector('.infinite-scroll-container');
    const groupingEnabled = container && container.dataset.productGroupingEnabled === 'true';
    const allowDuplicateGroupings = container && container.dataset.allowDuplicateGroupings === 'true';
    
    let appendedCount = 0;
    const appendedProducts = [];
    
    if (groupingEnabled && !allowDuplicateGroupings) {
      // Collect existing group handles from already-rendered cards
      const existingGroupHandles = new Set();
      this.productGrid.querySelectorAll('[data-group-handle]').forEach(el => {
        const gh = el.getAttribute('data-group-handle');
        if (gh) existingGroupHandles.add(gh);
      });
      
      // Filter and append, skipping products whose group is already shown
      newProducts.forEach((product) => {
        const cardEl = product.querySelector('[data-group-handle]');
        if (cardEl) {
          const groupHandle = cardEl.getAttribute('data-group-handle');
          if (groupHandle && existingGroupHandles.has(groupHandle)) {
            // Skip this duplicate grouped product
            return;
          }
          if (groupHandle) {
            existingGroupHandles.add(groupHandle);
          }
        }
        
        const clonedProduct = product.cloneNode(true);
        this.productGrid.appendChild(clonedProduct);
        appendedCount++;
        appendedProducts.push(clonedProduct);
      });
    } else {
      // No grouping, append all products
      newProducts.forEach((product) => {
        const clonedProduct = product.cloneNode(true);
        this.productGrid.appendChild(clonedProduct);
        appendedCount++;
        appendedProducts.push(clonedProduct);
      });
    }
    
    return { appended: appendedCount, products: appendedProducts };
  }

  collectSwatchDataFromContainer(container) {
    if (!container) return;
    
    const cards = container.querySelectorAll('[data-group-handle]');
    cards.forEach(card => {
      const groupHandle = card.getAttribute('data-group-handle');
      if (!groupHandle || this.swatchCache[groupHandle]) return;
      
      const swatchContainer = card.querySelector('[data-card-swatches]');
      if (!swatchContainer) return;
      
      const swatchButtons = swatchContainer.querySelectorAll('.card-swatch');
      if (swatchButtons.length === 0) return;
      
      const collectionHandle = swatchContainer.getAttribute('data-collection-handle') || '';
      
      const swatches = [];
      swatchButtons.forEach(btn => {
        swatches.push({
          handle: btn.getAttribute('data-product-handle') || '',
          url: btn.getAttribute('data-product-url') || '',
          style: btn.getAttribute('style') || '',
          label: btn.getAttribute('aria-label') || '',
          title: btn.getAttribute('title') || '',
        });
      });
      
      // Preserve the raw JSON string for the swatch-switching script tag
      const productDataScript = card.querySelector('[data-swatch-product-data]');
      let productDataJSON = null;
      if (productDataScript) {
        try {
          productDataJSON = productDataScript.textContent;
        } catch (e) {
          // non-critical
        }
      }
      
      this.swatchCache[groupHandle] = {
        collectionHandle,
        swatches,
        productDataJSON,
      };
    });
  }

  reconstructMissingSwatches(appendedProducts) {
    appendedProducts.forEach(product => {
      const card = product.querySelector('[data-group-handle]');
      if (!card) return;
      
      const groupHandle = card.getAttribute('data-group-handle');
      if (!groupHandle) return;
      
      // If swatch buttons already exist, nothing to do
      const existingSwatches = card.querySelector('[data-card-swatches]');
      if (existingSwatches && existingSwatches.querySelectorAll('.card-swatch').length > 0) {
        return;
      }
      
      const cachedData = this.swatchCache[groupHandle];
      if (!cachedData || cachedData.swatches.length === 0) return;
      
      const productHandle = card.getAttribute('data-product-handle') || '';
      const collectionHandle = cachedData.collectionHandle;
      
      // Build the swatch container
      const swatchDiv = document.createElement('div');
      swatchDiv.className = 'card-swatches';
      swatchDiv.setAttribute('data-card-swatches', '');
      swatchDiv.setAttribute('data-collection-handle', collectionHandle);
      
      // Put the current product's swatch first (active), then the rest
      const currentSwatch = cachedData.swatches.find(s => s.handle === productHandle);
      const otherSwatches = cachedData.swatches.filter(s => s.handle !== productHandle);
      const orderedSwatches = currentSwatch ? [currentSwatch, ...otherSwatches] : cachedData.swatches;
      
      orderedSwatches.forEach(swatch => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'card-swatch' + (swatch.handle === productHandle ? ' swatch--active' : '');
        btn.setAttribute('data-product-handle', swatch.handle);
        btn.setAttribute('data-product-url',
          collectionHandle
            ? '/collections/' + collectionHandle + '/products/' + swatch.handle
            : swatch.url
        );
        btn.setAttribute('style', swatch.style);
        btn.setAttribute('aria-label', swatch.label);
        btn.setAttribute('title', swatch.title);
        swatchDiv.appendChild(btn);
      });
      
      // Overflow link (hidden by default, used by swatch overflow logic)
      const overflowLink = document.createElement('a');
      overflowLink.className = 'swatch-overflow';
      overflowLink.setAttribute('data-swatch-overflow', '');
      overflowLink.href = collectionHandle
        ? '/collections/' + collectionHandle + '/products/' + productHandle
        : '#';
      overflowLink.style.display = 'none';
      overflowLink.setAttribute('aria-hidden', 'true');
      swatchDiv.appendChild(overflowLink);
      
      // Insert into the card DOM: before .card-info-link, inside .card-info-wrapper
      const cardInfoWrapper = card.querySelector('.card-info-wrapper');
      const cardInfoLink = card.querySelector('.card-info-link');
      
      if (cardInfoWrapper && cardInfoLink) {
        if (existingSwatches) existingSwatches.remove();
        cardInfoWrapper.insertBefore(swatchDiv, cardInfoLink);
        
        // Also inject the product data JSON so swatch-switching JS works
        if (cachedData.productDataJSON) {
          const script = document.createElement('script');
          script.type = 'application/json';
          script.setAttribute('data-swatch-product-data', '');
          script.textContent = cachedData.productDataJSON;
          cardInfoWrapper.insertBefore(script, cardInfoLink);
        }
        
        // The <list-card-product> custom element's connectedCallback already ran
        // (before swatches existed), so re-initialize swatch event handlers now.
        const listCardEl = product.querySelector('list-card-product');
        if (listCardEl && typeof listCardEl.initializeSwatches === 'function') {
          listCardEl.initializeSwatches();
        }
      }
    });
  }

  renderNewProductsDirect(appendedProducts) {
    // Reconstruct any missing swatches from the global cache.
    // Shopify limits metaobject references per request (~50 per type), so product
    // cards rendered beyond that limit have no swatch HTML. This fills them in
    // using swatch data cached from earlier renders.
    this.reconstructMissingSwatches(appendedProducts);
    
    // Apply the same rendering logic used in facets.js to already-appended DOM nodes
    appendedProducts.forEach(product => {
      // Handle scroll trigger animations (similar to facets.js)
      const scrollTriggers = product.querySelectorAll('.scroll-trigger');
      scrollTriggers.forEach(element => {
        element.classList.add('scroll-trigger--cancel');
      });
      
      // Handle lazy loading for images
      this.handleLazyLoading(product);
      
      // Trigger currency conversion if available
      if (window.convertCurrency) {
        window.convertCurrency(product);
      }
    });

    // Initialize scroll animations if enabled - pass the actual product grid container
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(this.productGrid);
    }

    // Reinitialize any product-specific JavaScript
    this.reinitializeProductScripts(appendedProducts);

    // Tell Yotpo to initialize widgets on newly added product cards
    this.refreshYotpoWidgets();
  }

  renderNewProducts(newProducts) {
    // Get the newly added products from the actual DOM
    const allProducts = this.productGrid.querySelectorAll('.grid__item');
    const newlyAddedProducts = Array.from(allProducts).slice(-newProducts.length);
    
    // Apply the same rendering logic used in facets.js
    newlyAddedProducts.forEach(product => {
      // Handle scroll trigger animations (similar to facets.js)
      const scrollTriggers = product.querySelectorAll('.scroll-trigger');
      scrollTriggers.forEach(element => {
        element.classList.add('scroll-trigger--cancel');
      });
      
      // Handle lazy loading for images
      this.handleLazyLoading(product);
      
      // Trigger currency conversion if available
      if (window.convertCurrency) {
        window.convertCurrency(product);
      }
    });

    // Initialize scroll animations if enabled - pass the actual product grid container
    if (typeof initializeScrollAnimationTrigger === 'function') {
      initializeScrollAnimationTrigger(this.productGrid);
    }

    // Reinitialize any product-specific JavaScript
    this.reinitializeProductScripts(newlyAddedProducts);
  }

  handleLazyLoading(container) {
    // Trigger lazy loading for dynamically loaded product images (from facets.js)
    if (window.lazyloadPolyfill) {
      window.lazyloadPolyfill(container);
    }
    
    // Also trigger lazysizes if available for lazyload class images (from facets.js)
    if (window.lazySizes) {
      // Trigger lazysizes to check for new lazyload elements
      container.querySelectorAll('img.lazyload').forEach((img) => {
        window.lazySizes.loader.unveil(img);
      });
      // Also trigger a general recheck for any missed elements
      window.lazySizes.autoSizer.checkElems();
    } else {
      // Fallback if lazySizes isn't loaded yet - manually process lazyload images
      container.querySelectorAll('img.lazyload').forEach((img) => {
        if (img.dataset.src && !img.src) {
          img.src = img.dataset.src;
        }
        if (img.dataset.srcset && !img.srcset) {
          img.srcset = img.dataset.srcset;
        }
        img.classList.remove('lazyload');
        img.classList.add('lazyloaded');
      });
      
      // Also handle images that might not have lazyload class but need src from data-src
      container.querySelectorAll('img[data-src]:not([src]), img[src=""]:not(.lazyloaded)').forEach((img) => {
        if (img.dataset.src) {
          img.src = img.dataset.src;
        }
        if (img.dataset.srcset) {
          img.srcset = img.dataset.srcset;
        }
      });
    }
  }

  showLoading() {
    // Get fresh reference to button
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    
    if (!this.loadMoreBtn) {
      console.warn('Load more button not found in showLoading');
      return;
    }
    
    this.loadMoreBtn.disabled = true;
    
    // Get fresh references to text elements
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    
    // Hide normal text
    if (this.loadText) {
      this.loadText.style.display = 'none';
    }
    
    // Show loading text
    if (this.loadingText) {
      this.loadingText.style.display = 'inline-block';
    } else {
      // Fallback: if loading text element not found, update button text directly
      this.loadMoreBtn.innerHTML = '<span class="loading-spinner"></span>LOADING...';
    }
    
    // Make sure loading overlay is hidden so products can be seen
    const loadingOverlay = scope.querySelector('.loading-overlay');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
  }

  hideLoading() {
    // Get fresh reference to button
    const scope = this.getScope();
    this.loadMoreBtn = scope.querySelector('[data-load-more]');
    
    if (!this.loadMoreBtn) {
      console.warn('Load more button not found in hideLoading');
      return;
    }
    
    this.loadMoreBtn.disabled = false;
    
    // Get fresh references to text elements
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    
    // Show normal text
    if (this.loadText) {
      this.loadText.style.display = 'inline-block';
    }
    
    // Hide loading text
    if (this.loadingText) {
      this.loadingText.style.display = 'none';
    } else {
      // Fallback: restore original button content
      const originalContent = `
        <span data-load-text>LOAD MORE</span>
        <span data-loading-text style="display: none;">
          <span class="loading-spinner"></span>LOADING...
        </span>
      `;
      this.loadMoreBtn.innerHTML = originalContent;
      
      // Re-select text elements after restoration
      this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
      this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
    }
  }

  updateCounters(newProductCount) {
    this.loadedProductCount += newProductCount;
    
    // Get fresh reference to counter in case it was replaced
    const scope = this.getScope();
    this.currentCountElement = scope.querySelector('[data-current-count]');
    
    if (this.currentCountElement) {
      this.currentCountElement.textContent = this.loadedProductCount;
    }
  }

  updateProgress() {
    // Get fresh references
    const scope = this.getScope();
    this.progressFill = scope.querySelector('[data-progress-fill]');
    this.totalCountElement = scope.querySelector('[data-total-count]');
    
    if (!this.progressFill || !this.totalCountElement) {
      return;
    }
    
    const totalCount = parseInt(this.totalCountElement.textContent) || 0;
    if (totalCount > 0) {
      const percentage = (this.loadedProductCount / totalCount) * 100;
      this.progressFill.style.width = `${Math.min(percentage, 100)}%`;
    }
  }

  updateLoadMoreButton(nextLoadMoreBtn) {
    // Update the button URL to point to the next page
    if (nextLoadMoreBtn.dataset.nextPageUrl) {
      let nextPageUrl = nextLoadMoreBtn.dataset.nextPageUrl;
      
      // Preserve wishlist parameter if it exists in the current URL
      const currentUrl = new URL(this.loadMoreBtn.dataset.nextPageUrl || window.location.href, window.location.origin);
      const hasWishlist = currentUrl.searchParams.has('wishlist');
      
      if (hasWishlist) {
        // Ensure the next URL also has the wishlist parameter
        const nextUrl = new URL(nextPageUrl, window.location.origin);
        if (!nextUrl.searchParams.has('wishlist')) {
          nextUrl.searchParams.set('wishlist', 'true');
          nextPageUrl = nextUrl.pathname + nextUrl.search;
        }
      }
      
      this.loadMoreBtn.dataset.nextPageUrl = nextPageUrl;
    }
    
    // Update data attributes if they exist
    if (nextLoadMoreBtn.dataset.nextPage) {
      this.loadMoreBtn.dataset.nextPage = nextLoadMoreBtn.dataset.nextPage;
    }
    if (nextLoadMoreBtn.dataset.loadedCount) {
      this.loadMoreBtn.dataset.loadedCount = nextLoadMoreBtn.dataset.loadedCount;
    }
    
    // Re-select text elements in case the button content changed
    this.loadingText = this.loadMoreBtn.querySelector('[data-loading-text]');
    this.loadText = this.loadMoreBtn.querySelector('[data-load-text]');
  }

  hideLoadMoreButton() {
    if (this.loadMoreBtn) {
      this.loadMoreBtn.style.display = 'none';
    }
  }

  showError() {
    const errorMessage = document.createElement('div');
    errorMessage.className = 'infinite-scroll-error';
    errorMessage.innerHTML = 'Failed to load more products. Please try again.';
    errorMessage.style.cssText = 'color: red; text-align: center; margin: 1rem 0;';
    
    this.loadMoreBtn.parentNode.insertBefore(errorMessage, this.loadMoreBtn);
    
    // Remove error message after 3 seconds
    setTimeout(() => {
      errorMessage.remove();
    }, 3000);
  }

  refreshYotpoWidgets() {
    // Yotpo v2 (widget.js) – re-scan the DOM for new .yotpo elements
    if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
      window.yotpo.refreshWidgets();
      return;
    }
    // Yotpo may not have assigned to window.yotpo yet; try the global API object
    if (typeof Yotpo !== 'undefined' && Yotpo.API) {
      try {
        new Yotpo.API(yotpo).refreshWidgets();
        return;
      } catch (_) { /* ignore */ }
    }
    // Yotpo v3 widget instances – trigger a custom event that the Yotpo
    // app embed listens to, or call its global init if exposed.
    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      window.yotpoWidgetsContainer.initWidgets();
    }
  }

  saveScrollState() {
    try {
      sessionStorage.setItem('collectionScrollState', JSON.stringify({
        url: window.location.pathname,
        scrollY: window.scrollY,
        lastPage: this.currentPage
      }));
    } catch(e) {}
  }

  async restoreScrollState() {
    let saved;
    try {
      const raw = sessionStorage.getItem('collectionScrollState');
      if (!raw) return;
      saved = JSON.parse(raw);
      if (saved.url !== window.location.pathname || saved.lastPage <= 1) {
        sessionStorage.removeItem('collectionScrollState');
        return;
      }
      sessionStorage.removeItem('collectionScrollState');
    } catch(e) { return; }

    try {
      this.isLoading = true;
      window.scrollRestoreInProgress = true;

      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      const nextPageUrlAttr = this.loadMoreBtn && this.loadMoreBtn.dataset.nextPageUrl;
      if (!nextPageUrlAttr) {
        this.isLoading = false;
        window.scrollRestoreInProgress = false;
        requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: 'instant' }));
        return;
      }

      // data-next-page-url carries all active filter params — use it as the base URL.
      const baseUrl = new URL(nextPageUrlAttr, window.location.origin);

      const pageUrls = [];
      for (let page = 2; page <= saved.lastPage; page++) {
        const url = new URL(baseUrl.href);
        url.searchParams.set('page', page);
        pageUrls.push(url.href);
      }

      // Fetch all pages in parallel.
      const responses = await Promise.all(
        pageUrls.map(url =>
          fetch(url, { method: 'GET', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        )
      );

      const htmlTexts = await Promise.all(
        responses.map(res => res.ok ? res.text() : Promise.resolve(null))
      );

      const parser = new DOMParser();
      const docs = htmlTexts.map(html => html ? parser.parseFromString(html, 'text/html') : null);

      // Append pages in order to preserve product sequence.
      let lastLoadMoreBtn = null;
      for (let i = 0; i < docs.length; i++) {
        const doc = docs[i];
        if (!doc) continue;

        const newProductGrid = doc.querySelector('#product-grid');
        if (!newProductGrid) continue;

        this.collectSwatchDataFromContainer(newProductGrid);

        const newProducts = newProductGrid.querySelectorAll('.grid__item');
        if (newProducts.length === 0) continue;

        const result = this.appendNewProducts(newProducts);
        if (result.appended > 0) {
          this.updateCounters(result.appended);
          this.updateProgress();
        }
        if (result.products.length > 0) {
          this.renderNewProductsDirect(result.products);
        }

        const pageLoadMoreBtn = doc.querySelector('[data-load-more]');
        if (pageLoadMoreBtn) lastLoadMoreBtn = pageLoadMoreBtn;

        this.currentPage++;
      }

      if (lastLoadMoreBtn) {
        this.updateLoadMoreButton(lastLoadMoreBtn);
      } else {
        this.hideLoadMoreButton();
      }

      this.isLoading = false;
      window.scrollRestoreInProgress = false;

      if (window._autoLoadPage2Timeout) {
        clearTimeout(window._autoLoadPage2Timeout);
        window._autoLoadPage2Timeout = null;
      }

      requestAnimationFrame(() => window.scrollTo({ top: saved.scrollY, behavior: 'instant' }));

    } catch(e) {
      this.isLoading = false;
      window.scrollRestoreInProgress = false;
    }
  }

  reinitializeProductScripts(newProducts) {
    // Reinitialize quick add functionality for new products
    if (window.theme && window.theme.quickAdd) {
      window.theme.quickAdd.init();
    }
    
    // Reinitialize product forms for new products
    newProducts.forEach(product => {
      // Skip products that have already been initialized
      if (product.dataset.initialized) return;
      
      const newProductForms = product.querySelectorAll('.product-form:not([data-initialized])');
      newProductForms.forEach(form => {
        if (window.theme && window.theme.ProductForm) {
          new window.theme.ProductForm(form);
          form.dataset.initialized = 'true';
        }
      });
      
      // Reinitialize any other product-specific functionality
      if (window.theme && window.theme.ProductCard) {
        const newProductCards = product.querySelectorAll('.card-product:not([data-initialized])');
        newProductCards.forEach(card => {
          window.theme.ProductCard.init(card);
          card.dataset.initialized = 'true';
        });
      }
      
      // Reinitialize swatch functionality
      const newSwatches = product.querySelectorAll('.product-option-swatch:not([data-initialized])');
      newSwatches.forEach(swatch => {
        if (window.theme && window.theme.ProductSwatch) {
          window.theme.ProductSwatch.init(swatch);
          swatch.dataset.initialized = 'true';
        }
      });

      // Reinitialize variant pickers
      const variantPickers = product.querySelectorAll('variant-selects:not([data-initialized])');
      variantPickers.forEach(picker => {
        if (window.customElements && window.customElements.get('variant-selects')) {
          picker.dataset.initialized = 'true';
        }
      });

      // Reinitialize quantity inputs
      const quantityInputs = product.querySelectorAll('quantity-input:not([data-initialized])');
      quantityInputs.forEach(input => {
        if (window.customElements && window.customElements.get('quantity-input')) {
          input.dataset.initialized = 'true';
        }
      });

      // Reinitialize modal triggers
      const modalTriggers = product.querySelectorAll('[data-modal-trigger]:not([data-initialized])');
      modalTriggers.forEach(trigger => {
        if (window.theme && window.theme.modals) {
          window.theme.modals.init(trigger);
          trigger.dataset.initialized = 'true';
        }
      });
      
      // Reinitialize details accordion (to fix the error)
      const detailsElements = product.querySelectorAll('details[id]:not([data-initialized])');
      detailsElements.forEach(details => {
        if (window.customElements && window.customElements.get('details-accordion')) {
          details.dataset.initialized = 'true';
        }
      });
      
      // Reinitialize back-in-stock buttons (set variants data from embedded script)
      const bisButtons = product.querySelectorAll('button.bis-link[data-variants=""]');
      bisButtons.forEach(button => {
        // Find the associated script tag that contains the variants data
        const scriptTag = button.parentElement.nextElementSibling;
        if (scriptTag && scriptTag.tagName === 'SCRIPT') {
          // Execute the script content to set the data-variants attribute
          try {
            // Extract the button ID and variants JSON from the script
            const scriptContent = scriptTag.textContent || scriptTag.innerHTML;
            // Use Function constructor to safely execute the script in current context
            const func = new Function(scriptContent);
            func();
          } catch (error) {
            console.warn('Failed to initialize BIS button variants:', error);
          }
        }
      });
      
      // Reinitialize CollectionBackInStock for new buttons
      if (window.collectionBackInStockInstance && window.collectionBackInStockInstance.reinit) {
        window.collectionBackInStockInstance.reinit();
      }
      
      // Mark this product as initialized
      product.dataset.initialized = 'true';
    });
  }
}

// Singleton pattern to prevent multiple instances
// Expose globally so collection tabs can reset it
window.infiniteScrollInstance = null;

function initInfiniteScroll() {
  // Only initialize if we have a load more button and haven't already initialized
  const loadMoreBtn = document.querySelector('[data-load-more]');
  
  if (loadMoreBtn && !window.infiniteScrollInstance) {
    window.infiniteScrollInstance = new InfiniteScroll();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initInfiniteScroll);
} else {
  // DOM is already loaded
  initInfiniteScroll();
}

// Also initialize for theme editor (but reset instance first)
document.addEventListener('shopify:section:load', () => {
  window.infiniteScrollInstance = null;
  initInfiniteScroll();
}); 